import OrderHeader from '../OrderHistoryDashboard/OrderHeader';
import { useState, useEffect, useCallback } from 'react'; 
import moment, { Moment } from 'moment';
import { useAppState } from '../../store/appState';
import { useOktaAuth } from '@okta/okta-react';
import getEmptyForNull from '../../utils/utils';
import { OrderHistoryInfo } from '../../adapters/types';
import { AgGrid } from '../../common/AgGrid';
import getAllOrders from '../../adapters/services/getAllOrders';
import { useToastProvider } from '../../providers/ToastProvider';
import TableToolbar from '../OrderHistoryDashboard/TableToolbar';
import { URL_ROUTES } from '../../adapters/constants';
import { useHistory } from 'react-router-dom';
import StatusIndicatorLocal from '../../common/StatusIndicatorLocal';
import { useInitializeGoogleTagManager } from '../../hooks/useInitializeGoogleTagManager';
import {
  StickySimpleTableContainer
} from '../../common/StyledComponents';
import { has } from 'lodash';

const DateFormat = 'YYYY-MM-DD HH:mm:ss'; // Define date format

const LandingPage = (): JSX.Element => {
  const { addErrorToast } = useToastProvider();
  const { authState } = useOktaAuth();
  window.dataLayer = window.dataLayer || [];
  
  const { selectedGroup, selectedStatus, setSelectedStatus, selectedFlag, setSelectedFlag, dateFrom, setDateFrom, dateTo, setDateTo } = useAppState();
  const [rowData, setRowData] = useState<OrderHistoryInfo[]>([]);
  const [orderData, setOrderData] = useState<OrderHistoryInfo[]>([]);
  const [searchedValue, setSearchedValue] = useState('');
  const [loader, setLoader] = useState(false);
  const history = useHistory();

  const onRowClick = (event: any) => {
    history.push(URL_ROUTES.partOrderDetailPage, { detail: event.data });
  };


  
 

  const convertToCSV = (
    data: OrderHistoryInfo[],
    columns: (keyof OrderHistoryInfo)[]
  ): string => {
    const csvRows: string[] = [];

    // header row
    csvRows.push(
      columns
        .map(col => col.charAt(0).toUpperCase() + col.slice(1))
        .join(',')
    );

    data.forEach(row => {
      const values = columns.map(column => {
        let value = row[column] ?? '';

        // if value looks like "number/number" Excel would treat as date, so force text
        if (typeof value === 'string' && /^\d+\/\d+$/.test(value)) {
          value = `="${value}"`;
        }

        // always escape quotes for CSV
        return `"${String(value).replace(/"/g, '""')}"`;
      });

      csvRows.push(values.join(','));
    });

    return csvRows.join('\n');
  };

  const onDownloadCSV = (): void => {
      const exportData = rowData.map(item => ({
    ...item,
    vdi: item.parts?.some((p: any) => p.vdiflag?.trim() === 'X') ? 'Yes' : 'No'
  }));
    const columns: (keyof OrderHistoryInfo)[] = ['purchaseOrderNumber', 'orderDate', 'createdDate', 'transmissionStatus' ,'customerPO', 'processed'];
    const csv = convertToCSV(exportData as any, [
    'purchaseOrderNumber',
    'orderDate',
    'createdDate',
    'transmissionStatus',
    'customerPO',
    'processed',
    'vdi' // our computed column
  ] as any);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'TransmittedOrders.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    window.dataLayer.push({
      'event': 'Report Downloaded'
    });
  };


  const headerProps = {
    headerTitle: '',
    hideBorder: false,
    hideTableTitle: false,
    id: `table-header`,
    isAlwaysExpanded: false,
    isDownloadable: false, // Enable CSV download
    isPrintable: false,
    isSearchable: false,
  };

  const columns = [
    { 
      field: 'createdDate', 
      headerName: 'Transmission Date and Time', 
      filter: 'agDateColumnFilter', 
      sortable: true,
      valueFormatter: (params: { value: moment.MomentInput }) => 
        params.value ? moment.utc(params.value).local().format(DateFormat) : '' 
    },
    { field: 'purchaseOrderNumber', headerName: 'PO Numbers' },
    { 
      field: 'orderDate', 
      headerName: 'Order Date', 
      filter: 'agDateColumnFilter', 
      sortable: true,      
      valueFormatter: (params: { value: moment.MomentInput }) => 
        params.value ? moment.utc(params.value).local().format('YYYY-MM-DD') : '' 
    },
    { field: 'customerPO', headerName: 'Manual PO Number' },
    { field: 'important', headerName: 'VDI', filter: true, sortable: true , valueGetter: (params: any) => 
      params.data?.parts?.some((part: any) => part.vdiflag?.trim() === 'X') ? 'Yes' : ''},
    { field: 'transmissionStatus', headerName: 'Transmission Status' ,cellRenderer: (params: any) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <StatusIndicatorLocal status={params.value} /> 
        <span>{params.value}</span>
      </div>
    )
  },
    { field: 'processed', headerName: 'Processed', sortable: true  },

  ];

  const fetchFilteredData = useCallback(async () => {
    setLoader(true);
    try {
      const groupValue = selectedGroup.groupName.value.replace(/-I/g, '');
      const data = await getAllOrders(
        `Bearer ${getEmptyForNull(authState?.accessToken?.accessToken)}`, 
        selectedGroup.groupName.storeId, 
        groupValue
      );
      window.dataLayer.push({
        'event': 'Group Updated'
      });
      setOrderData(data);
      setRowData(data);
    } catch (error : any) {           
      setOrderData([]);
      setRowData([]);
      addErrorToast('No Data Available for Selected Criteria');    
    } finally {
      setLoader(false);
    }
  }, [authState,selectedStatus,selectedGroup, selectedFlag, dateFrom, dateTo]);

  useEffect(() => {
    if (selectedGroup.groupName.storeId ) {
      fetchFilteredData();
    }
  }, [selectedGroup]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const hasInProgress = rowData.some(
        (item) => item.transmissionStatus === 'In-Progress'
      );
      console.log(hasInProgress);
      if (hasInProgress) {
        fetchFilteredData();
      }
    }, 120000); // 1 minute = 60000 ms
  
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [rowData, fetchFilteredData]);

  // --- keep imports etc. as is ---


// ✅ Utility function extracted outside of useEffect
const filterData = (
  orderData: OrderHistoryInfo[],
  searchedValue: string,
  selectedStatus: string[],
  orderDateFrom: Moment | null,
  orderDateTo: Moment | null,
  selectedFlag: string[]
): OrderHistoryInfo[] => {
  const lowerQuery = (searchedValue || '').toLowerCase();

  return (orderData || []).filter(item => {
    const poNum = (item.purchaseOrderNumber || '').toString().toLowerCase();
    const custPO = (item.customerPO || '').toString().toLowerCase();
    const orderDateStr = (item.orderDate || '').toString().toLowerCase();
    const itemDate = moment(item.orderDate, 'MM/DD/YYYY');

    const matchesSearch =
      poNum.includes(lowerQuery) ||
      custPO.includes(lowerQuery) ||
      orderDateStr.includes(lowerQuery);

    const matchesDate =
      (!orderDateFrom && !orderDateTo) ||
      itemDate.isBetween(moment(orderDateFrom).subtract(1, 'days'), moment(orderDateTo), null, '[]');

    const hasVDI = Array.isArray(item.parts)
      ? item.parts.some((part: any) => part.vdiflag === 'X')
      : false;

    const isNoFilter = selectedFlag.length === 0;
    const isBothSelected = selectedFlag.includes('Yes') && selectedFlag.includes('No');
    const isYesOnly = selectedFlag.includes('Yes');
    const isNoOnly = selectedFlag.includes('No');

    const flagConditions =
      isNoFilter ||
      isBothSelected ||
      (isYesOnly && hasVDI) ||
      (isNoOnly && !hasVDI);

    const matchesStatus =
      selectedStatus.length === 0 || selectedStatus.includes(item.transmissionStatus);

    return matchesSearch && matchesDate && flagConditions && matchesStatus;
  });
};


  useEffect(() => {
  setRowData(
    filterData(orderData, searchedValue, selectedStatus, dateFrom, dateTo, selectedFlag)
  );
}, [orderData, searchedValue, selectedStatus, dateFrom, dateTo, selectedFlag]);

  useInitializeGoogleTagManager(selectedGroup);

  return (
    <>
      <OrderHeader />
      <TableToolbar
        searchedValue={searchedValue}
        handleChange={setSearchedValue}
        handleDateDataChange={(startDate, endDate) => {
          setDateTo(endDate);
          setDateFrom(startDate);
        } }
        onDateClear={() => {
          setDateTo(null);
          setDateFrom(null);
        } }
        handleStatusFilter={setSelectedStatus}
        selectedStatus={selectedStatus}
        selectedFlag={selectedFlag}
        handleImportantFilter={setSelectedFlag} 
        onDownloadCSV={onDownloadCSV}      />
         <StickySimpleTableContainer >
      <AgGrid 
        columns={columns} 
        headerProps={headerProps} 
        rowData={rowData} 
        onRowClicked={onRowClick}
        getRowHeight={32}
        dataPageSize={10}
      />
      </StickySimpleTableContainer>
    </>
  );
};

export default LandingPage;
